import { useEffect } from "react";
import { Col, Row } from "reactstrap";
import { bankOptions, DEFAULT_OPTION } from "../../../../utils/Constant";
import SelectBox from "../../../../components/SelectBox";
import { Controller } from "react-hook-form";
import SearchBox from "../../../../components/SearchBox";
import FileUploadField from "../../../../components/FileUploadField";
import { fileValidationRules } from "../../../../utils/Util";
import MultiSelectDropdown from "../../../../components/MultiSelectDropdown";
import { useDispatch, useSelector } from "react-redux";
import {
  blocksByDistrictDataAPI,
  districtsByStateDataAPI,
  icvGetAllVillagesDataAPICall,
  icvGetDistrictsDataAPICall,
  icvGetMandalDataAPICall,
  villagesByBlocksAPI,
} from "../../../../api/icvActivityAPI";
import { SelectOptionType } from "../../../../models/GlobalModels";
import { getDistrictsDataAPICall } from "../../../../api/chmActivityAPI";
import { FormUtils } from "../../../../utils/FormUtils";

type CommonFieldsProps = {
  control: any;
  mode: string | undefined;
  surveyorType: { label: string; value: any };
  setOpenModal?: (openModal: boolean) => void;
  setValue: any;
  vendorNameOptions?: any[];
  watch: any;
};

const CommonFields = ({
  control,
  mode,
  setValue,
  watch,
  setOpenModal,
  surveyorType,
  vendorNameOptions,
}: CommonFieldsProps) => {
  const dispatch: any = useDispatch();
  const selectedState = watch("stateId");
  const selectedDistrict = watch("districtId");
  const selectedTehsilBlock = watch("tehsilBlockId");
  const vendorAllocationState = watch("allocations.0.stateId");
  const vendorAllocationDistrict = watch("allocations.0.districtId");
  const vendorAllocationTehsilBlock = watch("allocations.0.tehsilBlockId");
  const statusDropdownOptions = [
    { label: "Active", value: true },
    { label: "Inactive", value: false },
  ];
  const {
    storeICVGetStatesData,
    storeICVGetDistrictData,
    storeICVGetMandalData,
    storeICVGetVillagesData,
    storeICVDistrictsByStateData,
    storeICVBlocksByDistricData,

    closeModal,
  } = useSelector((state: any) => state.icvActivity);

  const vendorIndividualDocOptions = [
    { label: "SSC", value: "SSC" },
    { label: "HSC", value: "HSC" },
    { label: "GRADUATE", value: "GRADUATE" },
    { label: "POST-GRADUATE", value: "POST-GRADUATE" },
    { label: "OTHERS", value: "OTHERS" },
  ];

  const uploadFields = [
    {
      name: "educationCertificateBase64",
      label: "Highest Qualification Document",
      imageTitle: "Highest Qualification Document",
      rules: {
        required: "Document Image is required",
        validate: fileValidationRules.validate,
      },
      disabled: !watch("highestQualification")?.value,
    },
    {
      name: "panCardBase64",
      label: "PAN Card",
      imageTitle: "PAN Card",
      rules: {
        required: "Pan Card is required",
        validate: fileValidationRules.validate,
      },
    },
    {
      name: "aadhaarCardBase64",
      label: "Aadhaar Card",
      imageTitle: "Aadhaar Card",
      rules: {
        required: "Aadhaar Card is required",
        validate: fileValidationRules.validate,
      },
    },
    {
      name: "bankPassbookBase64",
      label: "Bank Passbook",
      imageTitle: "Bank Passbook",
      rules: {
        required: "Bank passbook is required",
        validate: fileValidationRules.validate,
      },
    },
    {
      name: "engagementLetterBase64",
      label: "Engagement Letter",
      imageTitle: "Engagement Letter",
      rules: {
        required: "Engagement Letter is required",
        validate: fileValidationRules.validate,
      },
    },
    {
      name: "passportPhotoBase64",
      label: "Passport Size Photograph",
      imageTitle: "Passport Size Photograph",
      rules: {
        required: "Passport Size Photograph is required",
        validate: fileValidationRules.validate,
      },
    },
  ];

  const statesData = FormUtils.convertDataList(
    storeICVGetStatesData?.data ?? [],
  );

  const districtData = FormUtils.convertDataList(
    storeICVGetDistrictData?.data ?? [],
  );
  const mandalData = FormUtils.convertDataList(
    storeICVGetMandalData?.data ?? [],
  );
  const villagesData = FormUtils.convertVillagesDataList(
    storeICVGetVillagesData?.data ?? [],
  );

  // Multi States, Districts and village - Vendor Allocation
  const multiDistrictsData = FormUtils.convertMultiDataDistrictList(
    storeICVDistrictsByStateData?.data ?? [],
  );

  const multiBlocksData = FormUtils.convertMultiBlocksList(
    storeICVBlocksByDistricData?.data ?? [],
  );

  // effects

  // Restricting the Districts  when state is removed from the state dropdown
  useEffect(() => {
    if (!multiDistrictsData?.length) return;

    const filteredDistricts: any = vendorAllocationDistrict.filter(
      (district: any) => {
        return vendorAllocationState.some(
          (state: any) => state.value === district.stateId,
        );
      },
    );

    setValue("allocations.0.districtId", filteredDistricts);
  }, [vendorAllocationState]);

  // Restricting the Tehsil/Blocks when district is removed from district dropdown
  useEffect(() => {
    if (!multiBlocksData?.length) return;

    const filteredBlocks: any = vendorAllocationTehsilBlock.filter(
      (block: any) => {
        return vendorAllocationDistrict.some(
          (district: any) => district.value === block.districtId,
        );
      },
    );

    setValue("allocations.0.tehsilBlockId", filteredBlocks);
  }, [vendorAllocationDistrict]);

  useEffect(() => {
    if (closeModal) {
      setOpenModal?.(false);
    }
  }, [closeModal]);

  // Handlers
  const onchangeDropdown = (
    label: string,
    event: any,
    fromAllocatedDetails?: boolean,
  ) => {
    switch (label) {
      case "State":
        if (!fromAllocatedDetails) {
          dispatch(icvGetDistrictsDataAPICall(event?.value));
        } else {
          const stateCodes: string[] = Array.from(
            new Set(event.map((item: SelectOptionType) => item.value)),
          );
          dispatch(districtsByStateDataAPI(stateCodes));
        }
        break;
      case "District":
        if (!fromAllocatedDetails) {
          if (typeof selectedState?.value === "string") {
            dispatch(
              icvGetMandalDataAPICall(selectedState?.value, event?.value),
            );
          }
        } else {
          const stateCodes: string[] = Array.from(
            new Set(event.map((item: any) => item.stateId)),
          );

          const districtCodes: string[] = Array.from(
            new Set(event.map((item: any) => item.value)),
          );

          dispatch(blocksByDistrictDataAPI(stateCodes, districtCodes));
        }

        break;
      case "tehsilBlock":
        if (!fromAllocatedDetails) {
          dispatch(
            icvGetAllVillagesDataAPICall(
              selectedState?.value,
              selectedDistrict?.value,
              event?.value,
            ),
          );
        } else {
          const stateCodes: string[] = Array.from(
            new Set(event.map((item: any) => item.stateId)),
          );

          const districtCodes: string[] = Array.from(
            new Set(event.map((item: any) => item.districtId)),
          );

          const blockCodes: string[] = Array.from(
            new Set(event.map((item: any) => item.value)),
          );
          dispatch(villagesByBlocksAPI(stateCodes, districtCodes, blockCodes));
        }
        break;
      default:
        dispatch(getDistrictsDataAPICall(event?.value));
        break;
    }
  };

  return (
    <>
      {mode === "edit" && (
        <Col xs={12} md={4} lg={3}>
          <Controller
            name="surveyorType"
            control={control}
            rules={{ required: "Surveyor type is required" }}
            render={({ field, fieldState: { error } }) => (
              <SelectBox
                {...field}
                label="Surveyor Type"
                options={[]}
                className="css-select-bg"
                required
                disabled={mode === "edit"}
              />
            )}
          />
        </Col>
      )}
      {/* Vendor Name */}
      {surveyorType.value === "SURVEYOR" && (
        <Col xs={12} md={4} lg={3}>
          <Controller
            name="vendorCode"
            control={control}
            rules={{
              required:
                surveyorType.value === "SURVEYOR"
                  ? "Vendor Name is required"
                  : false,
            }}
            render={({ field, fieldState: { error } }) => (
              <SelectBox
                {...field}
                label="Vendor Name"
                options={vendorNameOptions}
                className="css-select-bg"
                required={surveyorType?.value === "SURVEYOR"}
                disabled={mode === "edit"}
              />
            )}
          />
        </Col>
      )}
      {/* Surveyor First Name */}
      <Col xs={12} md={4} lg={3}>
        <Controller
          name="firstName"
          control={control}
          rules={{ required: "first name is required" }}
          render={({ field, fieldState: { error } }) => (
            <div style={{ position: "relative" }}>
              <SearchBox
                {...field}
                label="First Name"
                placeHolder="Enter First Name"
                maxLength={255}
                error={!!error}
                errorMessage={error?.message}
                required
                disabled={mode === "edit"}
              />
            </div>
          )}
        />
      </Col>

      {/* Surveyor Last Name */}
      <Col xs={12} md={4} lg={3}>
        <Controller
          name="lastName"
          control={control}
          rules={{ required: "last name is required" }}
          render={({ field, fieldState: { error } }) => (
            <div style={{ position: "relative" }}>
              <SearchBox
                {...field}
                label="Last Name"
                placeHolder="Enter Last Name"
                maxLength={255}
                error={!!error}
                errorMessage={error?.message}
                disabled={mode === "edit"}
                required
              />
            </div>
          )}
        />
      </Col>

      {/* Surveyor Father Name */}
      <Col xs={12} md={4} lg={3}>
        <Controller
          name="fatherOrRelativeName"
          control={control}
          rules={{ required: "Father Name is required" }}
          render={({ field, fieldState: { error } }) => (
            <div style={{ position: "relative" }}>
              <SearchBox
                {...field}
                label="Father/Relative Name"
                placeHolder="Enter Father/Relative Name"
                maxLength={255}
                error={!!error}
                errorMessage={error?.message}
                disabled={mode === "edit"}
                required
              />
            </div>
          )}
        />
      </Col>

      {/* Surveyor Email */}
      <Col xs={12} md={4} lg={3}>
        <Controller
          name="emailId"
          control={control}
          rules={{
            required:
              surveyorType?.value === "VENDOR_INDIVIDUAL"
                ? "Email is required"
                : false,

            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Enter a valid email address",
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <div style={{ position: "relative" }}>
              <SearchBox
                {...field}
                label="Email"
                placeHolder="Enter Email"
                error={!!error}
                errorMessage={error?.message}
                required={surveyorType.value === "VENDOR_INDIVIDUAL"}
              />
            </div>
          )}
        />
      </Col>

      {/* Mobile Number */}
      <Col xs={12} md={4} lg={3}>
        <Controller
          name="mobileNumber"
          control={control}
          rules={{
            required: "mobile number is required",
            pattern: {
              value: /^[6-9][0-9]{9}$/,
              message: "Enter a valid 10‑digit mobile number starting with 6–9",
            },
            minLength: {
              value: 10,
              message: "mobile number must be 10 digits",
            },
            maxLength: {
              value: 10,
              message: "mobile number must be 10 digits",
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <div style={{ position: "relative" }}>
              <SearchBox
                {...field}
                label="Mobile Number"
                placeHolder="Enter Mobile Number"
                error={!!error}
                maxLength={10}
                errorMessage={error?.message}
                disabled={mode === "edit"}
                onChange={(e: any) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 10);

                  field.onChange(value);
                }}
                required
              />
            </div>
          )}
        />
      </Col>

      {/* Address*/}
      <Col xs={12} md={4} lg={3}>
        <Controller
          name="address"
          control={control}
          rules={{ required: "Address is required" }}
          render={({ field, fieldState: { error } }) => (
            <div style={{ position: "relative" }}>
              <SearchBox
                required
                {...field}
                label="Address"
                placeHolder="Enter Address"
                error={!!error}
                errorMessage={error?.message}
              />
            </div>
          )}
        />
      </Col>

      {/* Status Dropdown */}
      <Col xs={12} md={4} lg={3}>
        <Controller
          name="isActive"
          control={control}
          rules={{ required: "Status is required" }}
          render={({ field, fieldState: { error } }) => (
            <SelectBox
              required
              {...field}
              label="Status"
              options={statusDropdownOptions}
              className="css-select-bg"
            />
          )}
        />
      </Col>

      {/* State Dropdown */}
      <Col xs={12} md={4} lg={3}>
        <Controller
          name="stateId"
          control={control}
          rules={{
            validate: (val) => (val?.value ? true : "State is required"),
          }}
          render={({ field, fieldState: { error } }) => (
            <SelectBox
              {...field}
              label="State"
              required
              options={statesData}
              className="css-select-bg"
              disabled={mode === "edit"}
              onChange={(event: any) => {
                if (event != null) {
                  setValue("stateId", event);
                  setValue("districtId", DEFAULT_OPTION);
                  setValue("tehsilBlockId", DEFAULT_OPTION);
                  setValue("village", DEFAULT_OPTION);

                  onchangeDropdown("State", event);
                } else {
                  field.onChange(DEFAULT_OPTION);
                  setValue("districtId", DEFAULT_OPTION);
                  setValue("tehsilBlockId", DEFAULT_OPTION);
                  setValue("village", DEFAULT_OPTION);
                }
              }}
            />
          )}
        />
      </Col>

      {/* District Dropdown */}
      <Col xs={12} md={4} lg={3}>
        <Controller
          name="districtId"
          control={control}
          rules={{
            validate: (val) => (val?.value ? true : "District is required"),
          }}
          render={({ field, fieldState: { error } }) => (
            <SelectBox
              {...field}
              disabled={selectedState?.label === "Select" || mode === "edit"}
              label="District"
              options={districtData}
              required
              onChange={(event: any) => {
                if (event != null) {
                  setValue("districtId", event);
                  setValue("tehsilBlockId", DEFAULT_OPTION);
                  setValue("village", DEFAULT_OPTION);

                  onchangeDropdown("District", event);
                } else {
                  field.onChange(DEFAULT_OPTION);
                  setValue("tehsilBlockId", DEFAULT_OPTION);
                  setValue("village", DEFAULT_OPTION);
                }
              }}
            />
          )}
        />
      </Col>

      {/* Mandal Dropdown */}
      <Col xs={12} md={4} lg={3}>
        <Controller
          name="tehsilBlockId"
          control={control}
          rules={{
            validate: (val) => (val?.value ? true : "tehsil/block is required"),
          }}
          render={({ field, fieldState: { error } }) => (
            <SelectBox
              {...field}
              disabled={selectedDistrict?.label === "Select" || mode === "edit"}
              label="Tehsil/Block"
              options={mandalData}
              className="css-select-bg"
              required
              onChange={(event: any) => {
                if (event != null) {
                  setValue("tehsilBlockId", event);
                  setValue("village", DEFAULT_OPTION);

                  onchangeDropdown("tehsilBlock", event);
                } else {
                  field.onChange(DEFAULT_OPTION);
                  setValue("village", DEFAULT_OPTION);
                }
              }}
            />
          )}
        />
      </Col>

      {/* Village Dropdown */}
      <Col xs={12} md={4} lg={3}>
        <Controller
          name="village"
          control={control}
          rules={{
            validate: (val) => (val?.value ? true : "Village is required"),
          }}
          render={({ field, fieldState: { error } }) => (
            <SelectBox
              {...field}
              disabled={
                selectedTehsilBlock?.label === "Select" || mode === "edit"
              }
              label="Village"
              options={villagesData}
              required
              className="css-select-bg"
              onChange={(event: any) => {
                if (event != null) {
                  setValue("village", event);
                } else {
                  field.onChange(DEFAULT_OPTION);
                }
              }}
            />
          )}
        />
      </Col>

      {/* PAN Number */}
      <Col xs={12} md={4} lg={3}>
        <Controller
          name="panNumber"
          control={control}
          rules={{
            required: "Village is required",
            pattern: {
              value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
              message: "Invalid PAN format (e.g. ABCDE1234F)",
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <div style={{ position: "relative" }}>
              <SearchBox
                {...field}
                label="PAN Number"
                placeHolder="Enter PAN Number"
                maxLength={10}
                required
                error={!!error}
                errorMessage={error?.message}
                onChange={(e: any) => {
                  const value = e.target.value.slice(0, 10);

                  field.onChange(value);
                }}
              />
            </div>
          )}
        />
      </Col>

      {/* AADHAAR Number */}
      <Col xs={12} md={4} lg={3}>
        <Controller
          name="aadhaarNumber"
          control={control}
          rules={{
            required: "Aadhaar number is required",
            minLength: {
              value: 12,
              message: "aadhaar number must be 12 digits",
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <div style={{ position: "relative" }}>
              <SearchBox
                required
                {...field}
                label="Aadhaar Number"
                placeHolder="Enter Aadhaar Number"
                error={!!error}
                maxLength={12}
                errorMessage={error?.message}
                onChange={(e: any) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 12);

                  field.onChange(value);
                }}
              />
            </div>
          )}
        />
      </Col>

      {/* Bank Name */}
      <Col xs={12} md={4} lg={3}>
        <Controller
          name="bankName"
          control={control}
          rules={{ required: "Bank name is required" }}
          render={({ field, fieldState: { error } }) => (
            <SelectBox
              {...field}
              label="Bank Name"
              options={bankOptions}
              className="css-select-bg"
              required
            />
          )}
        />
      </Col>

      {/* Branch Name */}
      <Col xs={12} md={4} lg={3}>
        <Controller
          name="branchName"
          control={control}
          rules={{
            required: "Branch name is required",
            pattern: {
              value: /^[A-Za-z\s]+$/,
              message: "Only alphabets are allowed",
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <div style={{ position: "relative" }}>
              <SearchBox
                {...field}
                label="Branch Name"
                placeHolder="Enter Branch Name"
                maxLength={255}
                error={!!error}
                errorMessage={error?.message}
                required
              />
            </div>
          )}
        />
      </Col>

      {/* Bank Account Number */}
      <Col xs={12} md={4} lg={3}>
        <Controller
          name="bankAccountNumber"
          control={control}
          rules={{ required: "Bank account number is required" }}
          render={({ field, fieldState: { error } }) => (
            <div style={{ position: "relative" }}>
              <SearchBox
                required
                {...field}
                label="Bank Account Number"
                placeHolder="Enter Bank Account Number"
                maxLength={25}
                error={!!error}
                errorMessage={error?.message}
                onChange={(e: any) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 25);

                  field.onChange(value);
                }}
              />
            </div>
          )}
        />
      </Col>

      {/* IFSC Code*/}
      <Col xs={12} md={4} lg={3}>
        <Controller
          name="ifscCode"
          control={control}
          rules={{
            required: "IFSC code is required",
            pattern: {
              value: /^[A-Z]{4}0[A-Z0-9]{6}$/,
              message: "Invalid IFSC format (e.g. SBIN0001234)",
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <div style={{ position: "relative" }}>
              <SearchBox
                {...field}
                label="IFSC Code"
                placeHolder="Enter IFSC Code"
                error={!!error}
                errorMessage={error?.message}
                required
              />
            </div>
          )}
        />
      </Col>

      {/* Device Id*/}
      <Col xs={12} md={4} lg={3}>
        <Controller
          name="deviceId"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <div style={{ position: "relative" }}>
              <SearchBox {...field} label="Device ID" placeHolder="" disabled />
            </div>
          )}
        />
      </Col>

      {/* Vendor Document Dropdown */}
      <Col xs={12} md={4} lg={3}>
        <Controller
          name="highestQualification"
          control={control}
          rules={{ required: "Document type is required" }}
          render={({ field, fieldState: { error } }) => (
            <SelectBox
              {...field}
              label="Highest Qualification Document"
              options={vendorIndividualDocOptions}
              className="css-select-bg"
              required
              disabled={mode === "edit"}
            />
          )}
        />
      </Col>

      {/* Image type note */}
      <Col className="mt-2" style={{ marginBottom: "-8px" }} xs={12}>
        <div className="upload-note-container mb-3">
          <h6 className="upload-note-title">Note:</h6>

          <ul className="upload-note-list mb-0">
            <li>Supported file types: JPG, JPEG, PNG, WEBP, PDF</li>
            <li>Maximum file size: 5MB</li>
          </ul>
        </div>
      </Col>

      {/* Document Upload Section */}
      {uploadFields.map((field) => (
        <FileUploadField
          key={field.name}
          name={field.name}
          control={control}
          label={field.label}
          imageTitle={field.imageTitle}
          rules={field.rules}
          disabled={field.disabled || mode === "edit"}
          required={field.rules?.required ? true : false}
        />
      ))}

      {/* Allocation Details Section */}

      <h1 className="sub-heading mt-2">Allocation Details</h1>

      {/* Allocation Multi State Dropdown */}
      <Col xs={12} md={4} lg={3}>
        <Controller
          name="allocations.0.stateId"
          control={control}
          rules={{ required: "State is required" }}
          render={({ field, fieldState: { error } }) => (
            <MultiSelectDropdown
              {...field}
              optional
              fromVendorCreation
              label="State"
              options={statesData}
              onChange={(event: any) => {
                if (event != null) {
                  setValue("allocations.0.stateId", event);
                  onchangeDropdown("State", event, true);
                } else {
                  setValue("allocations.0.stateId", [DEFAULT_OPTION]);
                }
              }}
            />
          )}
        />
      </Col>

      {/*Allocation District Dropdown */}
      <Col xs={12} md={4} lg={3}>
        <Controller
          name="allocations.0.districtId"
          control={control}
          rules={{ required: "District is required" }}
          render={({ field, fieldState: { error } }) => (
            <MultiSelectDropdown
              {...field}
              optional
              fromVendorCreation
              disabled={Number(vendorAllocationState.length) === 0}
              label="District"
              options={multiDistrictsData}
              onChange={(event: any) => {
                if (event != null) {
                  setValue("allocations.0.districtId", event, {
                    shouldValidate: true,
                  });
                  onchangeDropdown("District", event, true);
                } else {
                  setValue("allocations.0.districtId", [DEFAULT_OPTION]);
                }
              }}
            />
          )}
        />
      </Col>

      {/*Allocation Mandal Dropdown */}
      <Col xs={12} md={4} lg={3}>
        <Controller
          name="allocations.0.tehsilBlockId"
          control={control}
          rules={{ required: "Tehsil/Block is required" }}
          render={({ field, fieldState: { error } }) => (
            <MultiSelectDropdown
              {...field}
              fromVendorCreation
              disabled={Number(vendorAllocationDistrict.length) === 0}
              label="Tehsil/Block"
              optional
              options={multiBlocksData}
              onChange={(event: any) => {
                if (event != null) {
                  setValue("allocations.0.tehsilBlockId", event, {
                    shouldValidate: true,
                  });
                  onchangeDropdown("tehsilBlock", event, true);
                } else {
                  setValue("allocations.0.tehsilBlockId", [DEFAULT_OPTION]);
                }
              }}
            />
          )}
        />
      </Col>
    </>
  );
};

export default CommonFields;
